import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Area,
} from 'recharts';

const QuoteChart = ({ data, title = 'Daily Quote Analytics' }) => {
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [chartData, setChartData] = useState([]);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    if (data && data.length > 1) {
      try {
        const filteredRows = data.slice(1).filter(row => {
          const rowDate = new Date(row[0]);
          return rowDate.getMonth() + 1 === selectedMonth && rowDate.getFullYear() === selectedYear;
        });

        const dailyStats = {};

        filteredRows.forEach(row => {
          const rowDate = new Date(row[0]);
          const dayKey = new Date(rowDate.getFullYear(), rowDate.getMonth(), rowDate.getDate());

          if (!dailyStats[dayKey]) {
            dailyStats[dayKey] = 0;
          }

          dailyStats[dayKey] += Number(row[1]);
        });

        const formattedData = Object.entries(dailyStats)
          .map(([day, value]) => {
            const dateObj = new Date(day);
            return {
              name: dateObj.toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
              }),
              value: value,
              fullDate: dateObj,
            };
          })
          .sort((a, b) => a.fullDate - b.fullDate);

        if (formattedData.length > 0) {
          setChartData(formattedData);
          setHasData(true);
        } else {
          setChartData(getEmptyData());
          setHasData(false);
        }
      } catch (error) {
        console.error('Error processing chart data:', error);
        setChartData(getEmptyData());
        setHasData(false);
      }
    } else {
      setChartData(getEmptyData());
      setHasData(false);
    }
  }, [data, selectedMonth, selectedYear]);

  const getEmptyData = () => {
    const emptyData = [];
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(selectedYear, selectedMonth - 1, i);
      const formattedDate = date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
      });
      emptyData.push({
        name: formattedDate,
        value: 0,
        fullDate: date,
      });
    }

    return emptyData;
  };

  const getOptimizedChartData = () => {
    if (!hasData) {
      return chartData;
    }

    if (chartData.length <= 3) {
      const optimizedData = [...chartData];

      if (chartData.length === 1) {
        const singlePoint = chartData[0];
        const value = singlePoint.value;

        const prevDate = new Date(singlePoint.fullDate);
        prevDate.setDate(prevDate.getDate() - 1);

        const nextDate = new Date(singlePoint.fullDate);
        nextDate.setDate(nextDate.getDate() + 1);

        optimizedData.unshift({
          name: prevDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }),
          value: value * 0.3,
          fullDate: prevDate,
        });

        optimizedData.push({
          name: nextDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }),
          value: value * 0.7,
          fullDate: nextDate,
        });
      } else if (chartData.length === 2) {
        const [first, second] = chartData;
        const timeDiff = second.fullDate - first.fullDate;

        if (timeDiff < 3 * 24 * 60 * 60 * 1000) {
          const midDate = new Date((first.fullDate.getTime() + second.fullDate.getTime()) / 2);
          const midValue = (first.value + second.value) / 2;

          optimizedData.splice(1, 0, {
            name: midDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }),
            value: midValue,
            fullDate: midDate,
          });
        }
      }

      return optimizedData;
    }

    return chartData;
  };

  const handleMonthChange = e => {
    setSelectedMonth(Number(e.target.value));
  };

  const handleYearChange = e => {
    setSelectedYear(Number(e.target.value));
  };

  const optimizedData = getOptimizedChartData();

  return (
    <div className="w-full flex flex-col bg-white rounded-lg shadow-sm p-4 h-[250px] font-nohemi relative">
      {/* Header with Dropdown */}
      <div className="flex flex-row justify-end pb-2">
        <div className="flex space-x-2">
          {/* Month Dropdown */}
          <select
            className="border rounded p-1 text-sm text-[#4A5154] bg-[#F5F6F7]"
            value={selectedMonth}
            onChange={handleMonthChange}>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(2025, i))}
              </option>
            ))}
          </select>

          {/* Year Dropdown */}
          <select
            className="border rounded p-1 text-sm text-[#4A5154] bg-[#F5F6F7]"
            value={selectedYear}
            onChange={handleYearChange}>
            {Array.from({ length: 6 }, (_, i) => {
              const year = currentYear - i;
              return (
                <option key={year} value={year}>
                  {year}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={optimizedData} margin={{ top: 20, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0066FF" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#0066FF" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#E5E7E8" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: '#8A9296' }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={15}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#8A9296' }}
            axisLine={false}
            tickLine={false}
            domain={hasData ? ['dataMin - dataMin * 0.1', 'dataMax + dataMax * 0.1'] : [0, 100]}
            tickFormatter={value => {
              if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
              return value;
            }}
          />
          <Tooltip
            contentStyle={{
              fontSize: '12px',
              backgroundColor: 'white',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: 'none',
            }}
            formatter={value => [value.toLocaleString(), 'Value']}
          />
          {hasData ? (
            <Area
              type="monotone"
              dataKey="value"
              stroke="#0066FF"
              strokeWidth={2}
              fill="url(#colorValue)"
              activeDot={{ r: 6, fill: '#0066FF', stroke: 'white', strokeWidth: 2 }}
              connectNulls={false}
            />
          ) : (
            <>
              <Area type="monotone" dataKey="value" stroke="transparent" fill="transparent" />
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#8A9296"
                fontSize="14px"
                fontWeight="500"
                className="opacity-60">
                No Data Available
              </text>
            </>
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

QuoteChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.array),
  title: PropTypes.string,
};

export default QuoteChart;
